import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ROOMS } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-[#121212] border border-white/10 focus:border-[#d4af37] outline-none px-4 py-3 text-sm text-white placeholder:text-white/35 transition-colors duration-300";

export const BookingWidget = ({ compact = false, defaultRoom = "" }) => {
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", room_type: defaultRoom,
    check_in: "", check_out: "", adults: 2, children: 0, special_requests: "",
  });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.room_type) return toast.error("Please select a room type");
    setLoading(true);
    try {
      await axios.post(`${API}/reservations`, {
        ...form,
        adults: Number(form.adults),
        children: Number(form.children),
      });
      toast.success("Reservation enquiry sent! Our team will confirm within a few hours.");
      setForm({ full_name: "", email: "", phone: "", room_type: defaultRoom, check_in: "", check_out: "", adults: 2, children: 0, special_requests: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      className="bg-[#121212] border hairline p-6 md:p-10 shadow-[0_8px_32px_rgba(0,0,0,0.5)]"
      data-testid="booking-widget-form"
    >
      <p className="text-xs uppercase tracking-[0.3em] text-[#d4af37] mb-2">Reservation Enquiry</p>
      <h3 className="font-serif-display text-2xl sm:text-3xl font-light text-white mb-8">Book Your Stay</h3>

      <div className={`grid gap-4 ${compact ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"}`}>
        <input required value={form.full_name} onChange={set("full_name")} placeholder="Full name" className={inputCls} data-testid="booking-name-input" />
        <input required type="email" value={form.email} onChange={set("email")} placeholder="Email address" className={inputCls} data-testid="booking-email-input" />
        <input required value={form.phone} onChange={set("phone")} placeholder="Phone / WhatsApp" className={inputCls} data-testid="booking-phone-input" />
        <select required value={form.room_type} onChange={set("room_type")} className={inputCls} data-testid="booking-room-select">
          <option value="">Select room type</option>
          {ROOMS.map((r) => (
            <option key={r.slug} value={r.name}>{r.name}</option>
          ))}
        </select>
        <div>
          <label className="block text-[11px] uppercase tracking-widest text-white/40 mb-1">Check-in</label>
          <input required type="date" value={form.check_in} onChange={set("check_in")} className={inputCls} data-testid="booking-checkin-input" />
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-widest text-white/40 mb-1">Check-out</label>
          <input required type="date" value={form.check_out} onChange={set("check_out")} className={inputCls} data-testid="booking-checkout-input" />
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-widest text-white/40 mb-1">Adults</label>
          <input type="number" min="1" max="10" value={form.adults} onChange={set("adults")} className={inputCls} data-testid="booking-adults-input" />
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-widest text-white/40 mb-1">Children</label>
          <input type="number" min="0" max="10" value={form.children} onChange={set("children")} className={inputCls} data-testid="booking-children-input" />
        </div>
      </div>

      <textarea
        value={form.special_requests}
        onChange={set("special_requests")}
        placeholder="Special requests (airport transfer, celebration, dietary needs...)"
        rows={3}
        className={`${inputCls} mt-4 resize-none`}
        data-testid="booking-requests-input"
      />

      <button
        type="submit"
        disabled={loading}
        className="btn-gold mt-6 px-10 py-4 text-[12px] tracking-[0.2em] uppercase font-semibold disabled:opacity-60"
        data-testid="booking-submit-btn"
      >
        {loading ? "Sending..." : "Send Reservation Enquiry"}
      </button>
      <p className="mt-4 text-xs text-white/40">No prepayment required — our reservations team confirms availability and rates personally.</p>
    </form>
  );
};
